import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { AudioPlayer } from '@w/react-audio'
import type { QuestionBlock } from '@w/content-blocks'
import type { UFile } from '@w/utils'
import { Button } from '@w/ui/components/button'
import { UploadIcon, XIcon } from '@w/ui/components/icon'
import { IconButton } from '@w/ui/components/icon-button'
import { Input, useFileInput } from '@w/ui/components/input'
import { useMediaUrl } from '../../media-url-ctx'
import { useTranslation } from '../../i18n'
import type { QuestionAnswerValue } from '../../types/answers.types'
import { useBlockViewServices } from './services'

type SpokenQuestion = Extract<QuestionBlock, { subtype: 'sr' }>

export function SpokenReference({ block }: { block: SpokenQuestion }) {
  const { t } = useTranslation()
  const resolve = useMediaUrl()
  const { updateBlock, upload } = useBlockViewServices()
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const currentAnswer = useRef(block.answer)
  useLayoutEffect(() => { currentAnswer.current = block.answer }, [block.answer])
  async function uploadAudio(file: UFile) {
    setUploading(true)
    setError(null)
    try {
      await upload([file], { completed: (srcs, items) => {
        const src = srcs[0] ?? items[0]?.src
        if (!src) throw new Error('Audio upload returned no source')
        updateBlock(block.id, { answer: { ...currentAnswer.current, src } })
      } })
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : t('contentBlocksEditor.errors.fileUploadFailed'))
    } finally {
      setUploading(false)
    }
  }
  const [fileInputState, fileInputActions] = useFileInput({ fileType: 'audio', multiple: false, onFilesChange: files => {
    if (files[0]) void uploadAudio(files[0])
  } })
  return <div contentEditable={false} className="mt-1 flex min-w-0 flex-wrap items-center gap-2">
    {block.answer.src && <AudioPlayer className="max-w-full" minimalBelowMs={5_000} src={resolve(block.answer.src)} />}
    <Input aria-label="Attach spoken reference answer" className="min-h-0! w-fit border-0! bg-transparent! p-0!"
      data-testid={`question-sr-audio-${block.qid}`} fileInputActions={fileInputActions} fileInputState={fileInputState} minimal type="file">
      <Button disabled={uploading} semanticRole="secondary" size="sm" variant="text" onClick={fileInputActions.openFileDialog}>
        <UploadIcon />{uploading ? 'Uploading audio…' : block.answer.src ? 'Replace audio' : 'Attach audio'}
      </Button>
    </Input>
    {block.answer.src && <IconButton data-testid={`question-remove-sr-audio-${block.qid}`} semanticRole="secondary" size="sm" variant="text"
      onClick={() => { updateBlock(block.id, { answer: { ...block.answer, src: '' } }) }}><XIcon />Remove audio</IconButton>}
    {error && <span className="text-error text-sm" role="alert">{error}</span>}
  </div>
}

export function SpokenAnswer({ block, answer }: { block: SpokenQuestion; answer: QuestionAnswerValue }) {
  const { t } = useTranslation()
  const resolve = useMediaUrl()
  const learnerSrc = useBlobUrl(answer.audio?.blob)
  const [endedFor, setEndedFor] = useState<UFile | null>(null)
  return <div contentEditable={false} className="flex w-full min-w-0 flex-nowrap items-start" data-testid="flashcards-training-sr-comparison">
    <div className="flex min-w-0 flex-1 flex-col items-center gap-2 text-center">
      <p className="text-on-surface-secondary text-sm font-medium whitespace-nowrap">{t('contentBlocksEditor.document.referenceAnswer')}</p>
      {block.answer.src && <AudioPlayer autoplay className="max-w-full" fullWidth minimalBelowMs={5_000} src={resolve(block.answer.src)}
        onEnd={() => { setEndedFor(answer.audio ?? null) }} />}
    </div>
    <div className="flex min-w-0 flex-1 flex-col items-center gap-2 text-center">
      <p className="text-on-surface-secondary text-sm font-medium whitespace-nowrap">{t('contentBlocksEditor.document.yourAnswer')}</p>
      {learnerSrc && <AudioPlayer autoplay={!block.answer.src || answer.audio !== undefined && endedFor === answer.audio}
        className="max-w-full" fullWidth minimalBelowMs={5_000} src={learnerSrc} />}
    </div>
  </div>
}

function useBlobUrl(blob?: Blob) {
  const [url, setUrl] = useState<string>()
  useEffect(() => {
    let active = true
    let next: string | undefined
    queueMicrotask(() => {
      if (!active) return
      next = blob ? URL.createObjectURL(blob) : undefined
      setUrl(next)
    })
    return () => { active = false; if (next) URL.revokeObjectURL(next) }
  }, [blob])
  return url
}
